import { useEffect, useState } from "react";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import CustomInput from "../CustomInput";
import ModalLayout from "../layouts/ModalLayout";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { fetchStaff } from "../../features/staff/staffSlice";
import schedulePtmApis from "../../api/schedulePtm";
import { toast } from "sonner";
import { Ptm } from "../../types/SchedulePtm";
import { useLocation } from "react-router-dom";
import { useBranchCode } from "../../util/hooks/useBranch";

interface Props {
  onClose?: () => void;
}

export default function SelectPtmDateAndTime({ onClose }: Props) {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [openField, setOpenField] = useState<"date" | "time" | null>(null);
  const location = useLocation();
  const { ptm, isEdit } = location.state || {};

  const [formData, setFormData] = useState<Ptm>({
    scheduleDateTime: "",
    teacherInChargeId: "",
    coordinatorId: "",
  });

  const dispatch = useDispatch<AppDispatch>();
  const { staffList, filters } = useSelector((state: RootState) => state.staff);
  const selectedClassIds = useSelector(
    (state: RootState) => state.ptm.selectedClassIds,
  );

  const branchCode = useBranchCode();

  useEffect(() => {
    if (!isEdit || !ptm) return;

    const date = new Date(ptm.scheduleDateTime);

    setSelectedDate(date.toISOString().slice(0, 10));
    setSelectedTime(date.toISOString().slice(11, 16));

    setFormData({
      teacherInChargeId: Number(ptm.teacherInChargeId),
      coordinatorId: Number(ptm.coordinatorId),
      scheduleDateTime: ptm.scheduleDateTime,
    });
  }, [isEdit, ptm]);

  useEffect(() => {
    if (!branchCode) return;
    dispatch(
      fetchStaff({
        ...filters,
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [dispatch, filters, branchCode]);

  const getScheduleDateTime = () => {
    if (!selectedDate || !selectedTime) return "";
    return new Date(`${selectedDate}T${selectedTime}:00`).toISOString();
  };

  const handleSchedule = async (e: React.FormEvent) => {
    e.preventDefault();

    const payload = {
      branchCode,
      scheduleDateTime: getScheduleDateTime(),
      teacherInChargeId: Number(formData.teacherInChargeId),
      coordinatorId: Number(formData.coordinatorId),
      classIds: selectedClassIds,
    };

    try {
      if (isEdit) {
        await schedulePtmApis.updatePtm({
          ...payload,
          ptmId: ptm.ptmId,
        });
      } else {
        await schedulePtmApis.schedulePtm(payload);
      }

      onClose?.();
    } catch {
      // toast already handled
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  const isFormValid =
    !!selectedDate &&
    !!selectedTime 

  return (
    <ModalLayout onClose={onClose} width="max-w-lg">
      <div className="flex flex-col gap-7 md:max-h-[440px] overflow-y-auto custom-scrollbar">
        <span className="font-semibold text-xl text-neutral-400">
          Select Date/Time
        </span>

        <form className="flex flex-col gap-8" onSubmit={handleSchedule}>
          <div className="flex flex-col items-center justify-center divide-y divide-neutral-200">
            <div className="flex flex-col items-center justify-center">
              <span
                className="text-primary-600 text-lg font-semibold py-2 w-60 text-center cursor-pointer"
                onClick={() =>
                  setOpenField(openField === "date" ? null : "date")
                }
              >
                {selectedDate ? selectedDate : "Select date"}
              </span>

              {openField === "date" && (
                <div className="my-3 w-full">
                  <CustomInput
                    name=""
                    type="date"
                    value={selectedDate}
                    onChange={(e: any) => setSelectedDate(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col items-center justify-center">
              <span
                className="text-primary-600 text-lg font-semibold py-2 w-60 text-center cursor-pointer"
                onClick={() =>
                  setOpenField(openField === "time" ? null : "time")
                }
              >
                {selectedTime ? selectedTime : "Select time"}
              </span>

              {/* Time Input */}
              {openField === "time" && (
                <div className="my-3 w-full">
                  <CustomInput
                    name=""
                    type="time"
                    value={selectedTime}
                    onChange={(e: any) => setSelectedTime(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="border-t border-neutral-50 pt-4 flex flex-col gap-4">
            <span className="font-semibold text-xl text-neutral-400">
              Others
            </span>

            <div className="flex flex-col gap-7">
              <CustomSelect
                name="teacherInChargeId"
                label="PTM Teacher In-charge"
                value={formData.teacherInChargeId}
                onChange={handleInputChange}
                options={staffList.map((staff) => ({
                  label: staff.fullName,
                  value: String(staff.staffId),
                }))}
                placeholder="Select"
              />

              <CustomSelect
                name="coordinatorId"
                label="PTM Coordinator"
                value={formData.coordinatorId}
                onChange={handleInputChange}
                options={staffList.map((staff) => ({
                  label: staff.fullName,
                  value: staff.staffId,
                }))}
                placeholder="Select"
              />
            </div>
          </div>

          <div className="flex gap-4 justify-end items-end sticky bottom-0 bg-white pt-3">
            <CustomButton
              text="Cancel"
              color="bg-neutral-200"
              onClick={onClose}
            />
            <CustomButton
              text={isEdit ? "Update PTM" : "Schedule"}
              type="submit"
              color="bg-success-600"
              disabled={!isFormValid}
            />
          </div>
        </form>
      </div>
    </ModalLayout>
  );
}
